import React, { useState, useEffect, useCallback } from "react";
import "./Navbar.css";
import Modal from "react-modal";
import config from "@config";
import errorFunctions from "@store/errorFunctions";
import logo from "@img/subemo.webp";
import logo_r from "@img/logo_r32.png";
import {
  MdAccountCircle,
  MdExpandMore,
  MdNotifications,
  MdLock,
  MdPeople,
  MdSettings,
  MdHistory,
  MdExitToApp,
  MdBusiness,
  MdSupervisorAccount,
} from "react-icons/md";
import { NavLink, useHistory } from "react-router-dom";
import { slide as Menu } from "react-burger-menu";
import utilityFunctions from "@store/utilityFunctions";
import TimeMachine from "./TimeMachine";
import ResetPassword from "./ResetPassword";
import LoginAsUser from "./LoginAsUser";
import StaffSettings from "./StaffSettings";
import apiEndpoints from "@store/apiEndpoints";

const Navbar = ({
  protectedFetch,
  handleSelectedBrandID,
  userInfo,
  logout,
}) => {
  const history = useHistory();
  const [state, setState] = useState({
    showMenu: false,
    width: window.innerWidth,
    brands: [],
    isAdmin: false,
    isCommercial: false,
    isStaff: false,
    isContractor: false,
    isSCA: false,
    isApprover: false,
    isSuperAdmin: false,
    selectedBrandID:
      utilityFunctions.getCookie("brandId") ||
      utilityFunctions.getCookie("brandIds") ||
      0,
    mylogo: logo,
    showModalReport: false,
    showResetPwd: false,
    loading: false,
    brandSelected: null,
  });
  const [showMenuNotification, setShowMenuNotification] = useState(false);
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  // Helper method to safely parse JSON
  const parseUserInfo = useCallback(() => {
    try {
      const userInfoJson =
        localStorage.getItem("userInfo") ||
        utilityFunctions.getCookie("userInfo");
      if (!userInfoJson) return null;
      return JSON.parse(userInfoJson);
    } catch (error) {
      console.error("Error parsing user info JSON:", error);
      return null;
    }
  }, []);

  const retrieveBrands = useCallback(() => {
    setState((prev) => ({ ...prev, loading: true }));
    protectedFetch(
      config.backendServer + "brands/",
      "GET",
      null,
      errorFunctions.checkResponseThenJson,
    )
      .then((res) => {
        setState((prev) => ({ ...prev, brands: res }));

        // Only set default brand if no brand is currently selected
        const existingBrandId = utilityFunctions.getCookie("brandId");
        const existingBrandAll = utilityFunctions.getCookie("brandIds");

        if (!existingBrandId && existingBrandAll !== "all") {
          const brandId =
            config.country === "NZ"
              ? res.find((brand) => brand.name.includes("NZ")).id
              : res.find((brand) => !brand.name.includes("NZ")).id;
          handleSelectedBrandID(brandId);
        }

        if (res[0].id === "2") {
          setState((prev) => ({ ...prev, mylogo: logo }));
        } else if (res[0].id === "1") {
          setState((prev) => ({ ...prev, mylogo: logo_r }));
        }
      })
      .catch(() => {
        console.log("fail");
        setState((prev) => ({ ...prev, loading: false }));
      });
  }, [protectedFetch, handleSelectedBrandID]);

  const getNotifications = () => {
    setState((prev) => ({ ...prev, loading: true }));
    protectedFetch(
      apiEndpoints.notification.list(),
      "GET",
      { page: 1, page_size: 5 },
      errorFunctions.checkResponseThenJson,
    )
      .then((res) => {
        setNotifications(res.results || []);
        setState((prev) => ({ ...prev, loading: false }));
      })
      .catch(() => {
        console.log("fail");
        setNotifications([]);
        setState((prev) => ({ ...prev, loading: false }));
      });
  };

  const resize = useCallback(() => {
    setState((prev) => ({ ...prev, width: window.innerWidth }));
  }, []);

  const userInfoJson =
    localStorage.getItem("userInfo") || utilityFunctions.getCookie("userInfo");
  const userInfoParsed = userInfoJson && JSON.parse(userInfoJson);

  const role = userInfoParsed.user.role;

  useEffect(() => {
    getNotifications();
  }, []);

  useEffect(() => {
    window.addEventListener("resize", resize);
    retrieveBrands();

    setUser(userInfoParsed);
    if (userInfoParsed?.user) {
      const isPTC = !!userInfoParsed.user.service_center?.find((s) =>
        s.name.includes("PTC"),
      );
      const isAdmin = role === "Admin";
      const isSuperAdmin = [
        "william@treeclouds.com",
        "alfred@subemo.com",
      ].includes(userInfoParsed.user.email);
      const isApprover = role === "Approver";
      const isSCA = role === "Service Center Admin" && isPTC;
      const isCommercial = role.includes("Commercial");
      const isStaff = role === "Staff";
      const isContractor = role === "Contractor";

      setState((prev) => ({
        ...prev,
        isAdmin,
        isSCA,
        isStaff,
        isApprover,
        isContractor,
        isCommercial,
        isSuperAdmin,
      }));
    }

    return () => {
      window.removeEventListener("resize", resize);
    };
  }, [resize, retrieveBrands]);

  const openMenu = () => {
    if (!state.showMenu) {
      setState((prev) => ({ ...prev, showMenu: true }));
    }
  };

  const closeMenu = () => {
    if (state.showMenu) {
      setState((prev) => ({ ...prev, showMenu: false }));
    }
    closeMenuNotification();
  };

  const openMenuNotification = () => {
    if (!showMenuNotification) {
      setShowMenuNotification(true);
    }
  };

  const closeMenuNotification = () => {
    if (showMenuNotification) {
      setShowMenuNotification(false);
    }
  };

  const handleSettingsLinkClick = (linkToGoTo) => {
    setState((prev) => ({ ...prev, showMenu: false }));
    history.push(linkToGoTo);
  };

  const handleSelectedBrand = (e) => {
    let value = e.target.value;

    // Don't change the actual brandId for "all" option
    if (value !== "all") {
      handleSelectedBrandID(value);
      if (value === "2") {
        setState((prev) => ({ ...prev, mylogo: logo }));
      } else if (value === "1") {
        setState((prev) => ({ ...prev, mylogo: logo_r }));
      }
    } else {
      // For "all" option, just update the selected state without changing brandId
      handleSelectedBrandID("all");
    }

    setState((prev) => ({
      ...prev,
      selectedBrandID: value,
      brandSelected: value,
    }));
  };

  const getBrandOptions = () => {
    const parseJSON = parseUserInfo();
    if (!parseJSON?.user?.brands) return [];

    return parseJSON.user.brands
      .filter((elem) => elem.country === config.country)
      .map((brand) => {
        return { label: brand.name, value: brand.id };
      });
  };

  const renderLogoNavbar = () => {
    const brandOptions = getBrandOptions();
    return (
      <div className="NavbarLogoArea">
        <img alt="logo" className="NavbarLogo" src={logo} />
        <select
          value={state.selectedBrandID}
          onChange={handleSelectedBrand}
          className="NavbarBrandSelect"
        >
          <option value="all">All Brands</option>
          {brandOptions.map((brand) => (
            <option key={brand.value} value={brand.value}>
              {brand.label}
            </option>
          ))}
        </select>
      </div>
    );
  };

  let navbarLinks = {
    Tickets: "/home",
    Customer: "/customer-list",
    Inventories: "/inventory",
    Service_Centers: "/service-centers",
  };

  if (state.isStaff || state.isApprover) {
    navbarLinks = {
      Tickets: "/tickets",
      Customers: "/customer-list",
      Inventories: "/inventory",
      Devices: "/device",
      Service_Centers: "/service-centers",
      Stores: "/stores",

      // Quoted key with a literal space: the desktop renderer below does
      // .replace("_", " ") while the mobile menu prints the raw key, so an
      // underscore here would read "Ticket_New" on mobile.
      "Ticket New": "/ticket-new",
    };
  } else if (state.isAdmin) {
    navbarLinks = {
      Home: "/home",
      Tickets: "/tickets",
      Customers: "/customer-list",
      Inventories: "/inventory",
      Devices: "/device",
      Service_Centers: "/service-centers",
      Stores: "/stores",

      // Quoted key with a literal space: the desktop renderer below does
      // .replace("_", " ") while the mobile menu prints the raw key, so an
      // underscore here would read "Ticket_New" on mobile.
      "Ticket New": "/ticket-new",
    };
  } else if (state.isCommercial) {
    navbarLinks = {
      Tickets: "/commercial/tickets",
      Customers: "/commercial/customer-list",
      Commercial: "/commercial/staffs",
      // Sellers: "/sellers",
    };
  } else if (state.isSCA) {
    navbarLinks = {
      Home: "/home",
      Tickets: "/tickets",
      Customers: "/customer-list",
      Inventories: "/inventory",
      Service_Centers: "/service-centers",

      // Quoted key with a literal space: the desktop renderer below does
      // .replace("_", " ") while the mobile menu prints the raw key, so an
      // underscore here would read "Ticket_New" on mobile.
      "Ticket New": "/ticket-new",
    };
  } else if (state.isContractor) {
    navbarLinks = {
      Tickets: "/ticket-search",
    };
  }

  const additionalSettings = {};

  if (state.width > 850) {
    //Desktop menu
    return (
      <div
        className={
          "NavbarAboveContainer" /* To set max-width on navbarContainer while keeping a white background */
        }
        tabIndex="0"
        onClick={closeMenu}
        onBlur={
          closeMenu /*Tab Index and blur used so that clicks outside of div will cause menu to close*/
        }
      >
        <div className="NavbarContainer">
          {renderLogoNavbar()}
          <div className={"NavbarLinkArea"}>
            {Object.keys(navbarLinks).map(function (linkName) {
              return (
                <NavLink
                  key={"navbar" + linkName}
                  to={navbarLinks[linkName]}
                  className={"NavbarLink"}
                  activeClassName="NavbarLinkActive"
                  exact
                >
                  {linkName.replace("_", " ")}
                </NavLink>
              );
            })}
          </div>

          <div className={"NavbarDropdown"}>
            <div
              className="navbar-icon-btn mr-2"
              onClick={openMenuNotification}
            >
              <div className="navbar-notif-wrapper">
                <MdNotifications
                  size={22}
                  color={showMenuNotification ? "#0a3b85" : "#777"}
                />
              </div>
            </div>
            <div className="navbar-icon-btn" onClick={openMenu}>
              <MdAccountCircle
                color={state.showMenu ? "#0a3b85" : "#777"}
                size={22}
              />
              <MdExpandMore
                className={`NavbarDropdownIcon my-auto${state.showMenu ? " navbar-expand-open" : ""}`}
              />
            </div>
          </div>

          {state.showMenu && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="NavbarAccountDropdown"
            >
              <div className="NavbarAccountHeader">
                <MdAccountCircle size={34} color="#0a3b85" />
                <div className="NavbarAccountInfo">
                  <div className="NavbarAccountName">
                    <span title={userInfo?.user?.name || userInfo?.user?.email}>
                      {userInfo?.user?.name || userInfo?.user?.email}
                    </span>
                    {userInfo?.login_as_email && (
                      <span
                        className="NavbarAccountLoginAs"
                        title={userInfo.login_as_email}
                      >
                        ({userInfo.login_as_email})
                      </span>
                    )}
                  </div>
                  <span className="NavbarAccountRole">{role}</span>
                </div>
              </div>

              <div
                className="NavbarAccountItem"
                onClick={() =>
                  setState((prev) => ({ ...prev, showResetPwd: true }))
                }
              >
                <MdLock size={15} className="NavbarAccountItemIcon" />
                Reset My Password
              </div>

              {state.isAdmin && (
                <>
                  <div className="NavbarAccountDivider" />
                  <div
                    className="NavbarAccountItem"
                    onClick={() => handleSettingsLinkClick("/manage-staff")}
                  >
                    <MdPeople size={15} className="NavbarAccountItemIcon" />
                    Manage Staff
                  </div>
                  <div
                    className="NavbarAccountItem"
                    onClick={() => handleSettingsLinkClick("/default-settings")}
                  >
                    <MdSettings size={15} className="NavbarAccountItemIcon" />
                    Default Settings
                  </div>
                  <div
                    className="NavbarAccountItem"
                    onClick={() => handleSettingsLinkClick("/brand-settings")}
                  >
                    <MdBusiness size={15} className="NavbarAccountItemIcon" />
                    Brand Settings
                  </div>
                  {state.isSuperAdmin && (
                    <div
                      className="NavbarAccountItem"
                      onClick={() =>
                        setState((prev) => ({ ...prev, showModalReport: true }))
                      }
                    >
                      <MdHistory size={15} className="NavbarAccountItemIcon" />
                      Time Machine Report
                    </div>
                  )}
                </>
              )}

              <div className="NavbarAccountDivider" />
              <div
                className="NavbarAccountItem"
                onClick={() =>
                  setState((prev) => ({ ...prev, showStaffSettings: true }))
                }
              >
                <MdSettings size={15} className="NavbarAccountItemIcon" />
                Settings
              </div>

              {(state.isSuperAdmin ||
                user?.admin_userInfo?.user?.role === "Admin") && (
                <>
                  <div className="NavbarAccountDivider" />
                  <div
                    className="NavbarAccountItem"
                    onClick={() =>
                      setState((prev) => ({
                        ...prev,
                        showModalLoginAsUser: true,
                      }))
                    }
                  >
                    <MdSupervisorAccount
                      size={15}
                      className="NavbarAccountItemIcon"
                    />
                    {userInfo.is_login_as
                      ? `Sign Out as user${userInfo.login_as_email ? ` (${userInfo.login_as_email})` : ""}`
                      : "Login as user"}
                  </div>
                </>
              )}

              <div className="NavbarAccountDivider" />
              <div
                className="NavbarAccountItem NavbarAccountItem--danger"
                onClick={logout}
                onMouseDown={logout}
              >
                <MdExitToApp size={15} className="NavbarAccountItemIcon" />
                Sign Out
              </div>
            </div>
          )}

          {showMenuNotification && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="NavbarNotifDropdown"
            >
              <div className="NavbarNotifHeader">
                <span className="NavbarNotifTitle">Notifications</span>
              </div>
              <div className="NavbarNotifList">
                {notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <div key={notification.id} className="NavbarNotifItem">
                      <div className="NavbarNotifItemIcon">
                        <MdNotifications size={14} color="#0a3b85" />
                      </div>
                      <div className="NavbarNotifItemContent">
                        <p className="NavbarNotifItemText">
                          {notification.body}
                        </p>
                        <span className="NavbarNotifItemDate">
                          {new Date(notification.created).toLocaleString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="NavbarNotifEmpty">
                    <MdNotifications size={28} color="#ccc" />
                    <p>No notifications</p>
                  </div>
                )}
              </div>
              {/* <div
                className="NavbarNotifFooter"
                onClick={() => {
                  closeMenuNotification();
                  history.push("/notification-list");
                }}
              >
                View all notifications
                <MdOpenInNew size={13} />
              </div> */}
            </div>
          )}
        </div>
        <Modal isOpen={state.showModalReport} className={"input-modal"}>
          <TimeMachine
            handleCloseModal={() =>
              setState((prev) => ({ ...prev, showModalReport: false }))
            }
          />
        </Modal>

        <Modal isOpen={state.showResetPwd} className={"input-modal"}>
          <ResetPassword
            handleCloseModal={() =>
              setState((prev) => ({ ...prev, showResetPwd: false }))
            }
          />
        </Modal>

        <Modal isOpen={state.showModalLoginAsUser} className={"input-modal"}>
          <LoginAsUser
            handleCloseModal={() =>
              setState((prev) => ({ ...prev, showModalLoginAsUser: false }))
            }
            protectedFetch={protectedFetch}
            userInfo={userInfo}
          />
        </Modal>

        <Modal isOpen={!!state.showStaffSettings} className={"input-modal"}>
          <StaffSettings
            handleCloseModal={() =>
              setState((prev) => ({ ...prev, showStaffSettings: false }))
            }
            protectedFetch={protectedFetch}
          />
        </Modal>
      </div>
    );
  } else {
    return (
      <div
        className={
          "NavbarAboveContainer" /* To set max-width on navbarContainer while keeping a white background */
        }
      >
        <Menu>
          {Object.keys(navbarLinks).map(function (linkName) {
            return (
              <NavLink
                key={"navbar" + linkName}
                to={navbarLinks[linkName]}
                className={"NavbarLink"}
                activeClassName="NavbarLinkActive"
                exact
              >
                {linkName}
              </NavLink>
            );
          })}
          {Object.keys(additionalSettings).map(function (linkName) {
            return (
              <NavLink
                key={"navbar" + linkName}
                to={additionalSettings[linkName]}
                className={"NavbarLink"}
                activeClassName="NavbarLinkActive"
                exact
              >
                {linkName}
              </NavLink>
            );
          })}
          <div
            onClick={() => handleSettingsLinkClick("/manage-staff")}
            className={"NavbarLink"}
          >
            Manage Staff
          </div>

          <div onClick={logout} className={"NavbarLink"}>
            Sign Out
          </div>
        </Menu>
        <div className="NavbarContainer">{renderLogoNavbar()}</div>
      </div>
    );
  }
};

export default Navbar;
